/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { normalizeQuestions } from "cms/forms/context/form-reducer/reducer-actions/normalize-questions-helper";
import { produce } from "immer";
import { set } from "lodash";
import { useCallback, useState } from "react";
import isEqual from "react-fast-compare";
import { useCustomCompareEffect, useFirstMountState, useUpdateEffect } from "react-use";
import { generateGuid } from "utils/empty-exists";
import { listQuestions } from "../../forms/form-layout/step-questions-edit-v2/list-questions";
// import { useFormContext } from "../../forms/context/hooks/use-form-context";

/**
 * Manages CRUD operations on an array (or list) of objects.
 * 
 * Allows individual objects to be mutated without violating one-way data flow
 * practices.
 * 
 * @example
 * const initialItems = []
 * const { items, setItems, operations } = useMutableItemList(
 *     initialItems,
 *     'guid',
 *     (foo) => itemWithGuid({ foo }),
 *     (newItems) => Logger.debug(newItems)
 * )
 * const { add, update, patch, remove } = operations
 * 
 * @template {object} T
 * @template {keyof T} IdField
 * @template {Function | null} CreateItem
 * @param {T[]} initialItems - The initial list of items
 * @param {IdField} idField - Unique identifier for each object (e.g., guid)
 * @param {CreateItem} createItem - Optional factory function used to create a new item
 * @param {(items: T[]) => void} onListChange - Callback that executes whenever the list has changed
 * @param {boolean} [hasPosition=true] - Tracks if we should update the position or not
 */
export function useMutableItemList (
    initialItems,
    idField,
    createItem,
    onListChange,
    hasPosition = true
) {
    // const { state, dispatch } = useFormContext();
    
    /** @type {PersistableList<T>} */
    const initialState = {
        persist: "initial",
        list: initialItems
    };
    const [items, setItems] = useState(initialState);

    // Prevents an expensive call to setItems on first mount
    const isFirstMount = useFirstMountState();

    // save when persistence id changes
    useUpdateEffect(() => {
        // TODO: enable this log under a config
        // console.debug("[mutable-item-list] persist id has changed:", { data: items.persist });
        onListChange(items.list);
    }, [items.persist]);

    // update the local copy of items if we detect an external change
    useCustomCompareEffect(
        () => {
            if (!isFirstMount) {
                // Logger.debug("[mutable-item-list] items updated externally")
                // console.debug("[mutable-item-list] list internal state - items updated externally", items, initialItems);
                setItems((prev) => {
                    return {
                        ...prev,
                        list: initialItems
                    };
                });
            }
        },
        [initialItems],
        /**
         * This comparison function is critical for any and all list operations
         * in forms, composer, question groups, etc.
         * 
         * If an external change is not detected, subsequent updates will operate
         * against a stale cache which will overwrite data that was previously
         * saved with their original (default) values.
         */
        isEqual
    );
    
    const save = useCallback(() => {
        // for (const question of items.list) {
        //     const key = question.key;
        //     const position = question.position;
        //     console.info(`[useMutableItemList] save item ${position} ${key}`);
        // }

        /**
         * This update runs synchronously which is why we can safely mutate
         * newItem (adding its position).  Using the draft reference allows
         * use to keep this callback function pure.
         */

        setItems(
            produce((draft) => {
                const questions = draft.list;
                listQuestions(questions, "[useMutableItemList]");
                draft.persist = generateGuid();
                // dispatch({ type: "question/set", questions: questions });
                // dispatch({ type: "form/dirty", isFormDirty: true });
            })
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * @param {Parameters<CreateItem>} createItemArgs
     * @returns {CreateItem extends Function ? T : undefined}
     */
    const add = useCallback((...createItemArgs) => {
        if (!createItem) {
            console.warn("[mutable-item-list] cannot add an item since no default item was provided");
            return;
        }

        /** @type {T} */
        const newItem = createItem(...createItemArgs);

        /**
         * This update runs synchronously which is why we can safely mutate
         * newItem (adding its position).  Using the draft reference allows
         * use to keep this callback function pure.
         */
        setItems(
            produce((draft) => {
                let itemToAdd = newItem;
                
                if (hasPosition) {
                    // Create a new object with the position property instead of mutating the original
                    itemToAdd = {
                        ...newItem,
                        position: draft.list.length + 1
                    };
                }

                draft.list.push(itemToAdd);
                draft.persist = generateGuid();
            })
        );

        return newItem;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * @template {keyof T} K
     * @param {T[IdField]} id
     * @param {K | [K,...string[]]} key - Top level key, or subproperty syntax compatible with lodash.set
     * @param {T[K]} value
     */
    const update = useCallback((id, key, value) => {
        setItems(
            produce((draft) => {
                const item = draft.list.find((item) => {
                    return item[idField] === id;
                });

                if (item) {
                    set(item, key, value);
                    draft.persist = generateGuid();
                }
            })
        );
    }, [idField]);

    /**
     * Performs a shallow merge on an item with the given id.  It accepts a
     * partial object representing changes, but does not replace the id.
     */
    const patch = useCallback(
        /**
         * @param {T[IdField]} id
         * @param {Partial<Omit<T, IdField>>} patchItem
         */
        (id, patchItem) => {
            setItems(
                produce((draft) => {
                    const itemIndex = draft.list.findIndex((item) => item[idField] === id);

                    if (itemIndex >= 0) {
                        const item = draft.list[itemIndex];

                        draft.list[itemIndex] = {
                            ...item,
                            ...patchItem,
                            // ensure that the id is not replaced
                            [idField]: id
                        };
                        draft.persist = generateGuid();
                    } else {
                        console.warn(`[mutable-item-list] could not find an item with a ${idField} of ${id}`);
                    }
                })
            );
        }, [idField]
    );

    /**
     * @param {string} id
     */
    const remove = useCallback((id) => {
        setItems(
            produce((draft) => {
                const index = draft.list.findIndex((item) => item[idField] === id);

                if (index >= 0) {
                    draft.list.splice(index, 1);

                    if (hasPosition) {
                        // reorder positions from the index to the end of the array
                        for (let i = index; i < draft.list.length; i++) {
                            /** @type {import('immer').Draft<T & PositionalItem>} */
                            const item = draft.list[i];
                            item.position = i + 1;
                        }
                    }

                    draft.persist = generateGuid();
                }
            })
        );
    }, [idField, hasPosition]);

    /**
     * @param {Question} question
     */
    const push = useCallback((question) => {
        setItems(
            produce((draft) => {
                draft.list.push(question);
                normalizeQuestions(draft.list);
                draft.persist = generateGuid();
            })
        );
    }, []);

    const operators = {
        add,
        update,
        patch,
        remove,
        push,
        save
    };

    /** @type {[T[], typeof setItems, typeof operators]} */
    const stateAndOperatorTuple = [
        items.list,
        setItems,
        operators
    ];

    return stateAndOperatorTuple;
}
